import { z } from 'zod';
import {
    REPORT_CLASSES,
    REPORT_MENU_VISIBILITIES,
    REPORT_OUTPUT_FORMATS,
    REPORT_SCOPE_MODES,
    getReportDefinitionsForSurface,
} from './report-catalog.js';

const uniqueEnumArray = (values) => z.array(z.enum(values)).min(1).max(values.length).refine(
    (items) => new Set(items).size === items.length,
    { message: 'Values must be unique' },
);

const PublicReportSelectorSchema = z.object({
    properties: z.record(
        z.string().trim().min(1).max(120),
        z.enum(['tenant-owned-object-id', 'string', 'number', 'boolean']),
    ),
    required: z.array(z.string().trim().min(1).max(120)).max(50),
    additionalProperties: z.literal(false),
}).strict();

const PublicReportCapabilitiesSchema = z.object({
    profile: z.boolean(),
    profileOptional: z.boolean(),
    interactive: z.boolean(),
    schedule: z.boolean(),
    archive: z.boolean(),
    snapshot: z.boolean(),
    compare: z.boolean(),
    aiNarrative: z.boolean(),
    designer: z.boolean(),
}).strict();

export const ReportSurfaceItemSchema = z.object({
    reportKey: z.string().trim().min(1).max(120),
    definitionVersion: z.number().int().positive(),
    reportClass: z.enum(REPORT_CLASSES),
    labelKey: z.string().trim().min(1).max(160),
    descriptionKey: z.string().trim().min(1).max(160).optional(),
    order: z.number().int().min(0).max(1000),
    menuVisibility: z.enum(REPORT_MENU_VISIBILITIES).default('direct'),
    scopeModes: uniqueEnumArray(REPORT_SCOPE_MODES),
    outputFormats: uniqueEnumArray(REPORT_OUTPUT_FORMATS),
    visibleFields: z.array(z.string().trim().min(1).max(120)).min(1).max(100).refine(
        (items) => new Set(items).size === items.length,
        { message: 'Visible fields must be unique' },
    ),
    selectorSchema: PublicReportSelectorSchema,
    selectorDefaults: z.record(
        z.string().trim().min(1).max(120),
        z.union([z.string().max(500), z.number().finite(), z.boolean()]),
    ),
    profileContract: z.object({
        key: z.string().trim().min(1).max(120),
        version: z.number().int().positive(),
        optional: z.boolean(),
    }).strict().nullable(),
    artifact: z.object({
        archiveEnabled: z.boolean(),
        canonicalFormat: z.literal('pdf'),
    }).strict().nullable(),
    capabilities: PublicReportCapabilitiesSchema,
}).strict();

export function reportConfigurableFields(definition) {
    if (definition.renderer?.key !== 'static-repx-v1'
        || definition.renderer.dataShape !== 'object-array-root') {
        return definition.visibleFields;
    }
    const rootPrefix = `${definition.renderer.rootProperty}.`;
    const rootFields = definition.visibleFields
        .filter((field) => field.startsWith(rootPrefix))
        .map((field) => field.slice(rootPrefix.length));
    return rootFields.length
        ? rootFields
        : definition.visibleFields.filter((field) => !field.includes('.'));
}

export const ReportSurfaceDescriptorSchema = z.object({
    surfaceKey: z.string().trim().min(1).max(120),
    reports: z.array(ReportSurfaceItemSchema).max(100),
}).strict().superRefine((descriptor, ctx) => {
    const reportKeys = descriptor.reports.map((report) => report.reportKey);
    if (new Set(reportKeys).size !== reportKeys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reports'], message: 'Report keys must be unique within a surface' });
    }
    const orders = descriptor.reports.map((report) => report.order);
    if (new Set(orders).size !== orders.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reports'], message: 'Report order values must be unique within a surface' });
    }
});

export const ReportSurfaceCollectionSchema = z.object({
    surfaces: z.array(ReportSurfaceDescriptorSchema).min(1).max(100),
}).strict().superRefine(({ surfaces }, ctx) => {
    const surfaceKeys = surfaces.map((surface) => surface.surfaceKey);
    if (new Set(surfaceKeys).size !== surfaceKeys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['surfaces'], message: 'Surface keys must be unique' });
    }
});

export function buildReportSurfaceDescriptor(surfaceKey, { includeDisabled = false } = {}) {
    return ReportSurfaceDescriptorSchema.parse({
        surfaceKey,
        reports: getReportDefinitionsForSurface(surfaceKey, { includeDisabled }).map((definition) => ({
            reportKey: definition.key,
            definitionVersion: definition.definitionVersion,
            reportClass: definition.reportClass,
            labelKey: definition.presentation.labelKey,
            descriptionKey: definition.presentation.descriptionKey,
            order: definition.presentation.order,
            menuVisibility: definition.presentation.menuVisibility,
            scopeModes: definition.scopeModes,
            outputFormats: definition.outputFormats,
            visibleFields: reportConfigurableFields(definition),
            selectorSchema: definition.selectorSchema,
            selectorDefaults: definition.selectorDefaults,
            profileContract: definition.profile ? {
                key: definition.profile.schemaKey,
                version: definition.profile.schemaVersion,
                optional: definition.profile.optional,
            } : null,
            artifact: definition.artifact ? {
                archiveEnabled: definition.artifact.archiveEnabled,
                canonicalFormat: definition.artifact.canonicalFormat,
            } : null,
            capabilities: definition.capabilities,
        })),
    });
}
